import { Box, CheckBox, Image, ThemeContext } from 'grommet';
import infoIcon from '../../../../assets/BoundaryTool/info-icon.svg';
import { Feature } from '@turf/turf';
import { t, TFunction } from 'i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { StyledTooltip } from '../../../../components/common/StyledTooltip';
import React from 'react';

interface PracticeCheckboxProps {
  value: string;
  field: Feature;
  t: TFunction;
  saveField: (field: Feature) => void;
  country?: 'br' | 'us';
  disabled?: boolean;
  disabledForRegularFlow?: boolean;
  errored?: boolean;
  forceShow?: boolean;
  showTooltip?: boolean;
}

export const PracticeCheckbox = ({
  t,
  value,
  field: fieldParam,
  country,
  saveField,
  disabled,
  disabledForRegularFlow,
  errored,
  showTooltip,
}: PracticeCheckboxProps) => {
  const { usStackTillageWithLegumeAddition } = useFlags();
  const field = { ...fieldParam, properties: { ...fieldParam.properties } };

  field.properties = field.properties || {};
  field.properties.practices = field.properties.practices || [];
  return (
    <Box direction='row' height={'24px'}>
      <ThemeContext.Extend
        value={
          errored
            ? {
                checkBox: {
                  border: {
                    color: 'red',
                  },
                  color: 'red',
                  extend: () => `color: #FF0000;`,
                },
              }
            : {}
        }>
        <CheckBox
          data-cy={`${field.properties['name']}-practice-${value}`}
          disabled={disabled}
          style={{ fontSize: '14px', lineHeight: '19.12px' }}
          checked={field.properties.practices.indexOf(value) !== -1}
          onClick={() => {
            field.properties = field.properties || {};
            field.properties.practices = field.properties.practices || [];
            if (field.properties.practices.indexOf(value) !== -1) {
              field.properties.practices = field.properties.practices.filter((practice: string) => practice !== value);

              if (
                field.properties.practices.length === 1 &&
                field.properties.practices.find((p: any) => ['nitrogenReduction', 'inoculant'].indexOf(p) !== -1)
              ) {
                field.properties.practices = [];
              }
            } else {
              if (field.properties && field.properties.practices) {
                field.properties.practices = value === 'ICL' && country !== 'br' ? [value] : [...field.properties.practices, value];
              } else {
                field.properties.practices = [value];
              }
            }

            const isLegumeSelected = field?.properties?.practices?.indexOf('legume') !== -1;
            if (isLegumeSelected && field?.properties) {
              field.properties.practices = field.properties.practices.filter((practice: string) => {
                return (
                  practice === 'legume' ||
                  practice === 'nitrogenReduction' ||
                  (practice === 'reducedTillage' && usStackTillageWithLegumeAddition) ||
                  (practice === 'noTillage' && usStackTillageWithLegumeAddition)
                );
              });
              field.properties.paymentOption = 'OPTION_A';
            }
            saveField(field);
          }}
          label={t(`fieldFlow.fieldItem.practices.options.${value}`)}
        />
      </ThemeContext.Extend>

      {(disabledForRegularFlow || showTooltip) && (
        <StyledTooltip
          label={
            showTooltip
              ? t(`fieldFlow.fieldItem.practices.tooltip.${value}`)
              : `${t(`fieldFlow.fieldItem.practices.options.${value}`)} is not present in the contract`
          }
          showToolTip={true}
          dropOptions={'top'}>
          <Image
            height={24}
            width={24}
            style={{
              marginLeft: '5px',
              filter: 'brightness(0) saturate(100%) invert(15%) sepia(0%) saturate(0%) hue-rotate(179deg) brightness(99%) contrast(98%)',
            }}
            src={infoIcon}
          />
        </StyledTooltip>
      )}
    </Box>
  );
};

export const PracticeCheckboxColumn = ({ field, practices, saveField, country, practicesOnContract }: any) => {
  return (
    <Box width={'50%'} gap={'8px'}>
      {practices.map(
        ({ value, disabled, notInFlow, errored }) =>
          shouldShowCheckbox(value, field, country, practicesOnContract) && (
            <PracticeCheckbox
              key={`${field.id}-${value}`}
              field={field}
              saveField={saveField}
              t={t}
              value={value}
              disabled={disabled}
              disabledForRegularFlow={notInFlow}
              errored={errored}
            />
          ),
      )}
    </Box>
  );
};

export const shouldShowCheckbox = (checkboxValue: string, field: any, country: string, practicesOnContract: any): boolean => {
  const practiceToShortcodeLookup: any = {
    noTillage: 'NT',
    reducedTillage: 'IT',
    nitrogenReduction: 'NRR',
    nitrogenOptimization: 'NO',
    legume: 'LA',
    coverCropping: 'CC',
    fertilization: 'RPF',
    grazing: 'RPIG',
    seeding: 'RPBS',
    ICLP: 'ICL1',
    ICLR: 'ICL2',
    coverPerennial: 'CP',
    coverAnnual: 'CA',
    inoculant: 'IN',
  };
  const shortCode: string =
    practiceToShortcodeLookup[checkboxValue !== 'ICL' ? checkboxValue : checkboxValue + '' + field.properties?.practiceType[0]];
  if (!shortCode) return false;
  return country === 'br' ? true : practicesOnContract?.find((i: any) => i.code === shortCode);
};
